import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type {
  ChargerConfig,
  ChargerGroup,
  CountedPage,
  Driver,
  DriverGroup,
  Plan,
  Tariff,
} from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  plan: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PlanDialog({ open, plan, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Charger side (required: exactly one)
  const [chargerSide, setChargerSide] = useState<"charger" | "charger_group">("charger");
  const [chargerConfigId, setChargerConfigId] = useState<number | "">("");
  const [chargerGroupId, setChargerGroupId] = useState<number | "">("");

  // Eligibility (optional)
  const [eligibilitySide, setEligibilitySide] = useState<"none" | "driver" | "driver_group">("none");
  const [driverId, setDriverId] = useState<number | "">("");
  const [driverGroupId, setDriverGroupId] = useState<number | "">("");
  const [externalEligibilityWebhookUrl, setExternalEligibilityWebhookUrl] = useState("");

  const [tariffId, setTariffId] = useState<number | "">("");
  const [isActive, setIsActive] = useState(true);
  const [requiresCode, setRequiresCode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [chargers, setChargers] = useState<ChargerConfig[]>([]);
  const [chargerGroups, setChargerGroups] = useState<ChargerGroup[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverGroups, setDriverGroups] = useState<DriverGroup[]>([]);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);

  const toast = useToast();

  const loadOptions = useCallback(async () => {
    try {
      const [c, cg, d, dg, t] = await Promise.all([
        api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
        api<ChargerGroup[]>(`${MGMT}/charger-groups`),
        api<DriverListResponse>(`${MGMT}/drivers`),
        api<DriverGroup[]>(`${MGMT}/driver-groups`),
        api<Tariff[]>(`${MGMT}/tariffs`),
      ]);
      setChargers(c?.items ?? []);
      setChargerGroups(cg ?? []);
      setDrivers(fullDriversFromResponse(d));
      setDriverGroups(dg ?? []);
      setTariffs(t ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    loadOptions();
    if (plan) {
      setName(plan.name);
      setDescription(plan.description ?? "");
      setTariffId(plan.tariff_id ?? "");
      setIsActive(plan.is_active);
      setRequiresCode(plan.requires_code);
      setExternalEligibilityWebhookUrl(plan.external_eligibility_webhook_url ?? "");

      if (plan.charger_config_id) {
        setChargerSide("charger");
        setChargerConfigId(plan.charger_config_id);
        setChargerGroupId("");
      } else {
        setChargerSide("charger_group");
        setChargerGroupId(plan.charger_group_id ?? "");
        setChargerConfigId("");
      }

      if (plan.driver_id) {
        setEligibilitySide("driver");
        setDriverId(plan.driver_id);
        setDriverGroupId("");
      } else if (plan.driver_group_id) {
        setEligibilitySide("driver_group");
        setDriverGroupId(plan.driver_group_id);
        setDriverId("");
      } else {
        setEligibilitySide("none");
        setDriverId("");
        setDriverGroupId("");
      }
    } else {
      setName("");
      setDescription("");
      setChargerSide("charger");
      setChargerConfigId("");
      setChargerGroupId("");
      setEligibilitySide("none");
      setDriverId("");
      setDriverGroupId("");
      setTariffId("");
      setIsActive(true);
      setRequiresCode(false);
      setExternalEligibilityWebhookUrl("");
    }
  }, [open, plan, loadOptions]);

  const handleSave = async () => {
    const chargerOk = chargerSide === "charger" ? chargerConfigId !== "" : chargerGroupId !== "";
    if (!chargerOk) {
      toast.show("Select a charger or charger group", "error");
      return;
    }

    setSaving(true);
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      tariff_id: tariffId !== "" ? (tariffId as number) : undefined,
      charger_config_id: chargerSide === "charger" ? (chargerConfigId as number) : undefined,
      charger_group_id: chargerSide === "charger_group" ? (chargerGroupId as number) : undefined,
      driver_id: eligibilitySide === "driver" ? (driverId as number) : undefined,
      driver_group_id: eligibilitySide === "driver_group" ? (driverGroupId as number) : undefined,
      external_eligibility_webhook_url: externalEligibilityWebhookUrl.trim() || null,
      requires_code: requiresCode,
      is_active: isActive,
    };

    try {
      if (plan) {
        await api(`${MGMT}/plans/${plan.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.show("Plan updated");
      } else {
        await api(`${MGMT}/plans`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Plan created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{plan ? "Edit Plan" : "New Plan"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Divider>
            <Typography variant="caption" color="text.secondary">Charger Side (required)</Typography>
          </Divider>

          <Stack direction="row" spacing={2}>
            <Chip
              label="Specific Charger"
              onClick={() => setChargerSide("charger")}
              color={chargerSide === "charger" ? "primary" : "default"}
              variant={chargerSide === "charger" ? "filled" : "outlined"}
            />
            <Chip
              label="Charger Group"
              onClick={() => setChargerSide("charger_group")}
              color={chargerSide === "charger_group" ? "primary" : "default"}
              variant={chargerSide === "charger_group" ? "filled" : "outlined"}
            />
          </Stack>

          {chargerSide === "charger" ? (
            <FormControl fullWidth required>
              <InputLabel>Charger</InputLabel>
              <Select
                value={chargerConfigId}
                label="Charger"
                onChange={(e) => setChargerConfigId(e.target.value as number)}
              >
                {chargers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.display_name ?? c.charge_point_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Charger Group</InputLabel>
              <Select
                value={chargerGroupId}
                label="Charger Group"
                onChange={(e) => setChargerGroupId(e.target.value as number)}
              >
                {chargerGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Divider>
            <Typography variant="caption" color="text.secondary">Tariff (optional)</Typography>
          </Divider>

          <FormControl fullWidth>
            <InputLabel>Tariff (falls back to charger default)</InputLabel>
            <Select
              value={tariffId}
              label="Tariff (falls back to charger default)"
              onChange={(e) => setTariffId(e.target.value as number | "")}
            >
              <MenuItem value="">
                <em>Use charger default</em>
              </MenuItem>
              {tariffs.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} ({t.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider>
            <Typography variant="caption" color="text.secondary">Eligibility (optional — leave open for any driver)</Typography>
          </Divider>

          <Stack direction="row" spacing={2}>
            <Chip
              label="Open to All"
              onClick={() => { setEligibilitySide("none"); setDriverId(""); setDriverGroupId(""); }}
              color={eligibilitySide === "none" ? "primary" : "default"}
              variant={eligibilitySide === "none" ? "filled" : "outlined"}
            />
            <Chip
              label="Specific Driver"
              onClick={() => setEligibilitySide("driver")}
              color={eligibilitySide === "driver" ? "primary" : "default"}
              variant={eligibilitySide === "driver" ? "filled" : "outlined"}
            />
            <Chip
              label="Driver Group"
              onClick={() => setEligibilitySide("driver_group")}
              color={eligibilitySide === "driver_group" ? "primary" : "default"}
              variant={eligibilitySide === "driver_group" ? "filled" : "outlined"}
            />
          </Stack>

          {eligibilitySide === "driver" && (
            <FormControl fullWidth>
              <InputLabel>Eligible Driver</InputLabel>
              <Select
                value={driverId}
                label="Eligible Driver"
                onChange={(e) => setDriverId(e.target.value as number)}
              >
                {drivers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name} ({d.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {eligibilitySide === "driver_group" && (
            <FormControl fullWidth>
              <InputLabel>Eligible Driver Group</InputLabel>
              <Select
                value={driverGroupId}
                label="Eligible Driver Group"
                onChange={(e) => setDriverGroupId(e.target.value as number)}
              >
                {driverGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="External Eligibility Webhook URL (optional)"
            value={externalEligibilityWebhookUrl}
            onChange={(e) => setExternalEligibilityWebhookUrl(e.target.value)}
            fullWidth
            type="url"
            helperText="Called by backend eligibility verification for external access checks."
          />

          <Divider />

          <FormControlLabel
            control={<Switch checked={requiresCode} onChange={(e) => setRequiresCode(e.target.checked)} />}
            label="Require access code to subscribe"
          />

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {plan ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
